import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropType from 'prop-types';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ReactComponent as ChevronLeft } from '../../assets/images/svg/chevron-left.svg';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import IconButton from '../IconButton/IconButton';
import { closeWizardContent } from '../../util/helpers/wizardHelpers';
import { useOutsideClickHandler } from '../../util/hooks/useOutsideClickHandler';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { uFlexColumn, uTextEllipsis } from '../../assets/styles/utility';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';

const headerHeightDesktop = pxToRem(40);

const getModalSize = () => `
    width: 100%;
    max-width: 600px;
    min-height: ${pxToRem(650)};
  `;

export const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ zIndex }) => zIndex || variables.zIndex.indexXxl};
  background-color: ${themeColors.colorModalBackground};
  ${(props) => props.css};
  ${({ overModal }) =>
    overModal &&
    `
      background-color: transparent;
      z-index: ${variables.zIndex.indexXxxl};
    `};
`;

export const ModalElement = styled.div`
  ${uFlexColumn};
  box-shadow: ${variables.shadow.boxShadow};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorWhite};
  margin: ${headerHeightDesktop} auto;

  // margin: ${pxToRem(2)} auto;
  position: relative;
  overflow: hidden;
  max-height: calc(100vh - (2 * ${headerHeightDesktop}));
  width: 100%;
  max-width: 600px;
  height: ${({ fixedHeight }) =>
    fixedHeight
      ? `min(${pxToRem(650)}, calc(100vh - (2 * ${headerHeightDesktop})))`
      : 'auto'};

  ${({ size }) => getModalSize(size)}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    border-radius: 0;
    width: 100%;
    height: 100%;
    min-height: unset;
    max-height: 100vh;
    max-width: 100vw;
    margin: 0;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  z-index: ${variables.zIndex.indexXxs};
  padding: ${pxToRem(12)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: ${pxToRemMd(12)} ${pxToRemMd(16)};
  }
  // Doubled class, so it also beats the phone rule above.
  ${({ headerCss }) => headerCss && `&& { ${headerCss} }`};
`;

export const ModalClose = styled(IconButton)`
  &,
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    margin-left: auto;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    &,
    svg {
      width: ${pxToRemMd(24)};
      height: ${pxToRemMd(24)};
    }
  }
`;

export const ModalBack = styled(ModalClose)`
  margin-right: 5px;
  margin-left: 0;
  // base.js colours every svg directly, so set the arrow's own colour too.
  ${({ darkArrow }) =>
    darkArrow && `&, svg { color: ${themeColors.colorTextPrimary}; }`}

  &,
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const ModalBodyWrap = styled.div`
  ${uFlexColumn}
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

export const ModalTitle = styled.h3`
  ${uTextEllipsis};
  font-size: ${({ smallTitle }) => (smallTitle ? '16px' : '20px')};
  font-weight: 600;
  line-height: 1.3;
  padding-right: ${pxToRem(10)};
  margin-right: auto;
  ${({ isGS }) => isGS && `font-family: ${variables.family.goldSilver};`}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
    line-height: 1.3;
  }
  // Doubled class, so it also beats the phone rule above.
  ${({ titleCss }) => titleCss && `&& { ${titleCss} }`};
`;

const BasicModal = ({
  isOpen,
  children,
  overlay,
  goBack,
  close,
  closeIcon,
  title,
  size,
  overModal,
  avoidClearingStorage,
  handleOutsideClick,
  skipListeningToLocations,
  isPopupOpen,
  dataCy,
  fixedHeight,
  zIndex,
  smallTitle,
  headerCss,
  titleCss,
  darkArrow,
}) => {
  const brokerCode = useBrokerCode();
  const history = useHistory();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.removeAttribute('style');
    };
  }, [isOpen]);

  const closeModal = () => {
    if (!avoidClearingStorage) {
      closeWizardContent();
    }

    if (typeof close === 'function') {
      close();
    }
  };

  useEffect(() => {
    if (history && !skipListeningToLocations) {
      return history.listen(() => closeModal());
    }

    return () => {
      if (history) {
        history.listen(() => undefined);
      }
    };
  }, []); //eslint-disable-line

  const modalRef = useRef(null);

  useOutsideClickHandler(modalRef, close, handleOutsideClick);

  return isOpen
    ? ReactDOM.createPortal(
        <ModalContainer overModal={overModal} isOpen={isOpen} zIndex={zIndex}>
          <ModalElement
            size={size}
            ref={modalRef}
            data-cy={dataCy}
            fixedHeight={fixedHeight}
          >
            {!isPopupOpen && (
              <>
                {title && (
                  <ModalHeader headerCss={headerCss}>
                    {goBack && (
                      <ModalBack onClick={() => goBack()} darkArrow={darkArrow}>
                        <ChevronLeft />
                      </ModalBack>
                    )}
                    {title && (
                      <ModalTitle
                        isGS={brokerCode === 'GS'}
                        smallTitle={smallTitle}
                        titleCss={titleCss}
                      >
                        {title}
                      </ModalTitle>
                    )}
                    <ModalClose
                      onClick={closeModal}
                      data-cy="button-close-modal"
                    >
                      {closeIcon || <Close />}
                    </ModalClose>
                  </ModalHeader>
                )}
                <ModalBodyWrap>{children}</ModalBodyWrap>
                {overlay}
              </>
            )}
          </ModalElement>
        </ModalContainer>,
        document.getElementById('root'),
      )
    : null;
};

BasicModal.propTypes = {
  children: PropType.node,
  overlay: PropType.node,
  isOpen: PropType.oneOfType([PropType.bool, PropType.shape({})]),
  goBack: PropType.oneOfType([PropType.func, PropType.bool]),
  close: PropType.func,
  closeIcon: PropType.node,
  closeIconVisible: PropType.bool,
  title: PropType.string,
  size: PropType.oneOf(['sm', 'md', 'lg']),
  className: PropType.string,
  isLoading: PropType.bool,
  avoidClearingStorage: PropType.bool,
  handleOutsideClick: PropType.bool,
  skipListeningToLocations: PropType.bool,
  popupTitle: PropType.string,
  popupMessage: PropType.string,
  popUpPrimaryButtonText: PropType.string,
  onClosePopup: PropType.func,
  isPopupOpen: PropType.bool,
  dataCy: PropType.string,
  fixedHeight: PropType.bool,
  zIndex: PropType.number,
  smallTitle: PropType.bool,
  // Extra CSS for the header and title. It wins over their defaults at every width.
  headerCss: PropType.string,
  titleCss: PropType.string,
  darkArrow: PropType.bool,
};

export default BasicModal;
